/**
 * The single client-side path for saving a new identity's display name as a
 * draft profile.
 *
 * Three flows used to rebuild the same payload around the canonical
 * `saveDraftProfile` in `lib/bap/backup.ts`, each with its own failure policy.
 * One policy applies everywhere now: draft saving is non-fatal, these helpers
 * never throw, and callers log a warning and continue.
 */

import { saveDraftProfile } from "@/lib/bap/backup";
import { STORAGE_KEYS } from "@/lib/storage-keys";

export interface InitialProfileData {
	alternateName?: string;
	bio?: string;
}

/**
 * The profile data captured by the signup form, or null when none was
 * captured (imports/restores), the value is unparseable, or there is no
 * sessionStorage (server render).
 */
export function readInitialProfileData(): InitialProfileData | null {
	if (typeof sessionStorage === "undefined") {
		return null;
	}
	const raw = sessionStorage.getItem(STORAGE_KEYS.INITIAL_PROFILE_DATA);
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") {
			return null;
		}
		return parsed as InitialProfileData;
	} catch {
		return null;
	}
}

/**
 * The display name captured by the signup form, trimmed, or null when none
 * was captured or it is blank.
 */
export function readInitialDisplayName(): string | null {
	const data = readInitialProfileData();
	if (!data) {
		return null;
	}
	const name = String(data.alternateName ?? "").trim();
	return name || null;
}

/** Persist the signup form's profile data for the post-sign-in flows. */
export function writeInitialProfileData(data: InitialProfileData): void {
	if (typeof sessionStorage === "undefined") {
		return;
	}
	sessionStorage.setItem(
		STORAGE_KEYS.INITIAL_PROFILE_DATA,
		JSON.stringify(data)
	);
}

/** Drop the captured profile data once it has been persisted server-side. */
export function clearInitialProfileData(): void {
	if (typeof sessionStorage === "undefined") {
		return;
	}
	sessionStorage.removeItem(STORAGE_KEYS.INITIAL_PROFILE_DATA);
}

/**
 * Save an identity's display name (and bio) as its draft profile. Reports
 * failure instead of throwing so callers can keep going.
 */
export async function saveIdentityDraft(
	bapId: string,
	data: InitialProfileData
): Promise<{ success: boolean; error?: string }> {
	const identity: Record<string, unknown> = {};
	if (data.alternateName !== undefined) {
		identity.alternateName = data.alternateName;
	}
	if (data.bio !== undefined) {
		identity.bio = data.bio;
	}

	try {
		return await saveDraftProfile(bapId, { identity });
	} catch {
		return { success: false, error: "Failed to save draft profile" };
	}
}

/**
 * Save the signup form's captured profile data as the draft profile for a
 * freshly-registered identity.
 *
 * The captured data is cleared ONLY after a save the server accepted: a sync
 * that runs before the session exists gets a 401, and the typed name must
 * survive for the post-sign-in registration (OPL-4238).
 *
 * @returns true when a draft was saved (and the captured data cleared).
 */
export async function saveInitialProfileDraft(bapId: string): Promise<boolean> {
	const data = readInitialProfileData();
	if (!data) {
		return false;
	}

	const result = await saveIdentityDraft(bapId, data);
	if (!result.success) {
		return false;
	}

	clearInitialProfileData();
	return true;
}
